import React, { useEffect } from "react";
import { useLocation, Routes, Route, matchPath } from "react-router";
import { useParams } from "react-router-dom";
import { css, useTheme } from "@emotion/react";
import { useQuery } from "@apollo/client";
import { Helmet } from "react-helmet-async";

import { getCanRenderWebp } from "core/includes/image";
import { useAppState } from "core/contexts/AppContext";
import useHeaderHeight from "core/hooks/useHeaderHeight";
import useMetadata, { MetaData } from "core/hooks/useMetadata";
import pageQuery, { getEventRegistration } from "core/includes/queries";
import { retrieveValue } from "core/includes/localStorage";
import { getFontStylesForCss } from "theme/fontSizes";

import FormContainer from "core/Components/Form/FormContainer";
import ModalHeader from "core/Components/Modal/ModalHeader";
import SectionContainer from "core/Components/SectionContainer";
import Wrapper from "core/Components/Wrapper";
import Loading from "core/Components/Utils/Loading";

import EventRegistrationPage from "pages/Components/EventRegistrationPage";
import DetailConfirmation from "pages/Components/EventRegistration/DetailConfirmation";
import ErrorPage from "pages/Components/ErrorPage/ErrorPage";

type Props = {
    showBreadcrumb: boolean,
    hideHeaderCountry: boolean,
};

const EventRegistrationRouter: React.FC<Props> = ({ showBreadcrumb, hideHeaderCountry }) => {
    const theme = useTheme();
    const { eventId } = useParams();
    const canRenderWebp = getCanRenderWebp();
    const [appState, setAppState] = useAppState();
    const headerHeight = useHeaderHeight({ showBreadcrumb, hideHeaderCountry });
    const location = useLocation();
    const isRegisterPage = !!matchPath('/event-registration/:eventId', location.pathname);

    const userCode = localStorage.getItem('userCode');

    const { data, loading, error } = useQuery(pageQuery, {
        variables: { id: eventId, urlSegment: '', canRenderWebp },
        context: {
            headers: {
                'Selected-Country': appState.countryCode.code || (retrieveValue("selected-country") ?? "")
            }
        },
    });

    const page = data?.page;
    const metaData = useMetadata(page);

    // Set the breadcrumb label
    useEffect(() => {
        setAppState((prev: any) => ({
            currentPageName: page?.title ?? prev.currentPageName,
            loading: false
        }));
    }, [page?.title, setAppState]);

    const { data: registrantsQueryData, refetch } = useQuery(getEventRegistration, {
        variables: {
            eventId,
            userCode,
            hasCheckedOut: false,
            canRenderWebp,
        },
    });

    useEffect(() => {
        refetch();
    }, [isRegisterPage, refetch]);

    const existingRegistrationData = registrantsQueryData?.eventRegistration?.[0]
    const registrantsData = existingRegistrationData?.registrants;

    const wrapperStyle = css`
        padding-top: calc(${headerHeight}px + 2%);

        ${theme.breakpoints.only('md')} {
            padding-top: calc(${headerHeight}px + 3%);
        }

        ${theme.breakpoints.only('sm')} {
            padding: calc(${headerHeight}px + 5%) 22px 0;
        }

        ${theme.breakpoints.only('xs')} {
            padding: 138px 10px 0;
        }
    `;

    const titleStyle = css`
        text-align: center;
        margin-bottom: 28px;

        ${getFontStylesForCss('2')};

        ${theme.breakpoints.only('lg')} {
            width: 774px;
        }

        ${theme.breakpoints.only('md')} {
            width: 678px;
            margin-bottom: 28px;
        }

        ${theme.breakpoints.down('xs')} {
            margin-bottom: 25px;
        }
    `;

    return (
        <>
            {error && (
                <ErrorPage error={error.message === "404" ? error.message : "500"} />
            )}
            {!error && (
                <>
                    <Helmet>
                        {metaData.map((meta: MetaData, index) => {
                            switch (meta.element) {
                                case 'link': return <link key={`meta-key-${index}`} {...meta.props} />;
                                case 'title': return <title key={`meta-key-${index}`} {...meta.props} />;
                                default: return <meta key={`meta-key-${index}`} {...meta.props} />;
                            }
                        })}
                    </Helmet>
                    <SectionContainer data={{ backgroundColour: "white" }}>
                        <Wrapper
                            css={wrapperStyle}
                            useMaxWidth={false}
                            flexDirection={"column"}
                            justifyContent={"start"}
                        >
                            <FormContainer>
                                {loading ? (
                                    <Loading onTop />
                                ) : (
                                    <>
                                        <ModalHeader css={titleStyle}>{page?.title}</ModalHeader>
                                        <Routes>
                                            <Route
                                                path={"/"}
                                                element={
                                                    <EventRegistrationPage
                                                        eventPage={data.page}
                                                        registrantsData={registrantsData}
                                                        userCode={userCode}
                                                    />
                                                }
                                            />
                                            <Route
                                                path={"/detail-confirmation"}
                                                element={
                                                    existingRegistrationData ?
                                                        <DetailConfirmation
                                                            eventPage={data.page}
                                                            existingRegistrationData={existingRegistrationData}
                                                            userCode={userCode}
                                                        /> : null
                                                }
                                            />
                                        </Routes>
                                    </>
                                )}
                            </FormContainer>
                        </Wrapper>
                    </SectionContainer>
                </>
            )}
        </>
    );
};

export default EventRegistrationRouter;